import type { ComponentProps, ReactNode } from 'react';
import SpinnerThreeDots from '@/shared/ui/SpinnerThreeDots';

export default function Button({
  variant = 'primary',
  size = 'sm',
  loading = false,
  leftIcon,
  rightIcon,
  children,
  className,
  disabled,
  ...rest
}: {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
} & ComponentProps<'button'>) {
  const key = `${variant}-${size}`;
  const isIconOnly = children == null && leftIcon == null && rightIcon == null;
  const spinnerSize = size === 'sm' ? 3 : 4;
  const spinnerColor =
    variant === 'primary' ? '#ffffff' : 'var(--purple-default)';

  return (
    <button
      type="button"
      disabled={(disabled ?? false) || loading}
      className={[
        base,
        sizeClasses[size],
        radiusClasses[variant],
        variantClasses[variant],
        loading ? `relative ${loadingClass}` : disabledClass,
        isIconOnly ?
          size === 'sm' ?
            'size-8'
          : 'size-10'
        : paddingClasses[key],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {/* Loading must not change the button's width: the content keeps its
          box. */}
      <span
        className={[
          'flex items-center justify-center',
          gapClasses[key],
          loading && 'invisible',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {leftIcon}
        {children}
        {rightIcon}
      </span>
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <SpinnerThreeDots color={spinnerColor} size={spinnerSize} />
        </span>
      )}
    </button>
  );
}

type Variant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'secondary-rounded'
  | 'main-rounded'
  | 'text';

type Size = 'sm' | 'lg';

const base =
  'inline-flex items-center justify-center cursor-pointer shrink-0 transition-colors';

const sizeClasses: Record<Size, string> = {
  sm: 'h-8 text-body-md font-medium',
  lg: 'h-10 text-body-lg font-bold',
};

const gapClasses: Record<string, string> = {
  'primary-sm': 'gap-xs',
  'primary-lg': 'gap-s',
  'secondary-sm': 'gap-s',
  'secondary-lg': 'gap-s',
  'tertiary-sm': 'gap-s',
  'tertiary-lg': 'gap-s',
  'secondary-rounded-sm': 'gap-xs',
  'secondary-rounded-lg': 'gap-s',
  'main-rounded-sm': 'gap-xs',
  'main-rounded-lg': 'gap-s',
  'text-sm': 'gap-s',
  'text-lg': 'gap-s',
};

const paddingClasses: Record<string, string> = {
  'primary-sm': 'px-m',
  'primary-lg': 'px-l',
  'secondary-sm': 'px-m',
  'secondary-lg': 'px-l',
  'tertiary-sm': 'px-m',
  'tertiary-lg': 'px-l',
  'secondary-rounded-sm': 'px-l',
  'secondary-rounded-lg': 'px-l',
  'main-rounded-sm': 'px-l',
  'main-rounded-lg': 'px-l',
  'text-sm': 'px-l',
  'text-lg': 'px-l',
};

const radiusClasses: Record<Variant, string> = {
  primary: 'rounded-sm',
  secondary: 'rounded-sm',
  tertiary: 'rounded-sm',
  'secondary-rounded': 'rounded-2xl',
  'main-rounded': 'rounded-2xl',
  text: 'rounded-xs',
};

const variantClasses: Record<Variant, string> = {
  primary:
    'bg-purple-default text-white hover:bg-purple-hover active:bg-purple-hover',
  secondary:
    'bg-background-main text-gray-text hover:bg-purple-01 hover:text-purple-default active:bg-purple-01 active:text-purple-default',
  tertiary:
    'border border-gray-4 text-gray hover:text-purple-default active:text-purple-default',
  'secondary-rounded':
    'border border-gray-4 text-gray hover:bg-purple-01 hover:border-purple-default hover:text-purple-default active:bg-purple-01 active:border-purple-default active:text-purple-default',
  'main-rounded':
    'bg-gray-6 text-gray-text hover:text-purple-default active:text-purple-default',
  text: 'text-gray hover:text-purple-default active:text-purple-default',
};

const disabledClass =
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none';
const loadingClass = 'opacity-70 pointer-events-none';
